import { useCallback, useEffect, useMemo, useState } from 'react'
import { deviceTimeZone } from '../shared/clock'
import { ApiError, api, type State } from './api'
import { newBuildAvailable, pollMs } from './freshness'
import { routeFromSearch } from './deeplink'
import { PushBanner } from './PushBanner'
import { Recap } from './screens/Recap'
import { SeasonStrip, TabBar, type Tab } from './nav'
import { PullToRefresh } from './PullToRefresh'
import { AddGame } from './screens/AddGame'
import { AdminBoard } from './screens/AdminBoard'
import { AdminPlayers } from './screens/AdminPlayers'
import { Picks } from './screens/Picks'
import { Profile } from './screens/Profile'
import { Season } from './screens/Season'
import { Setup } from './screens/Setup'
import { ThisWeek } from './screens/ThisWeek'
import { Empty } from './ui'
import type { SeasonRow } from './api'

/*
 * The shell, and the way in.
 *
 * Three doors, and only the first two are usually seen:
 *
 *   1. The family code. Once per device, then a long-lived cookie. Without it
 *      the site would be world-writable.
 *   2. The player picker, then a splash that says the name back before any
 *      picking happens. That splash is not ceremony - picking as the wrong kid
 *      is this app's easiest mistake, and one confirmation tap is the cheapest
 *      thing that prevents it.
 *   3. A PIN, but only for a player who has one set. Everybody starts without.
 */



export default function App() {
  const [state, setState] = useState<State | null>(null)
  const [gate, setGate] = useState<'loading' | 'join' | 'pick-player' | 'splash' | 'ready'>('loading')
  const [pending, setPending] = useState<{ id: string; name: string } | null>(null)
  /*
   * WHERE A NOTIFICATION TAP LANDS.
   *
   * Read once, before first paint, so the app opens on the screen the push was
   * about rather than flashing the Board and jumping. The query string is then
   * wiped from the address bar - a reload should not re-navigate somebody who
   * has since walked to another tab, and a shared URL should not carry
   * somebody else's notification with it.
   */
  const opened = useMemo(() => routeFromSearch(window.location.search), [])
  const [tab, setTab] = useState<Tab>(opened?.tab ?? 'Board')
  /*
   * Admin is two screens plus a third you reach from one of them, so it keeps
   * its own little bit of state rather than becoming three more tabs. Five is
   * already the most a bottom bar should carry.
   */
  const [adminView, setAdminView] = useState<'board' | 'players' | 'recap' | 'add-game'>(
    opened?.admin ?? 'board',
  )

  useEffect(() => {
    if (opened) window.history.replaceState(null, '', window.location.pathname)
  }, [opened])
  const [season, setSeason] = useState<SeasonRow[]>([])
  const [week, setWeek] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const tz = useMemo(() => deviceTimeZone(), [])

  const load = useCallback(async () => {
    try {
      const s = await api.state(week ?? undefined)
      setState(s)
      /*
       * The chosen player sticks. The server sets a 30-day player cookie, so a
       * reload lands straight on the board and the picker is only ever seen
       * again by tapping the name in the masthead.
       */
      setGate(s.me.playerId ? 'ready' : 'pick-player')
      if (s.me.playerId) setPending(null)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setGate('join')
      else setError(e instanceof Error ? e.message : 'Could not load.')
    }
  }, [week])

  useEffect(() => { void load() }, [load])

  /*
   * The season is loaded here rather than inside the strip, because the strip
   * appears on four screens and would otherwise refetch the whole season every
   * time somebody changed tab.
   */
  const loadSeason = useCallback(() => {
    /*
     * A failure KEEPS the last good table rather than emptying it. Clearing it
     * made the pinned strip silently vanish mid-session - the strip renders
     * nothing on an empty array - so one hiccup deleted a piece of furniture
     * that is supposed to be on every screen, and only a reload brought it
     * back.
     */
    void api.season().then((r) => setSeason(r.rows)).catch(() => {})
  }, [])
  useEffect(() => { loadSeason() }, [loadSeason])

  /*
   * STABLE, and it has to be.
   *
   * As a bare arrow this was a new function on every render. `Picks` takes it
   * as `onChanged`, its `drain` callback depends on it, and its effect depends
   * on `drain` - so every render rebuilt the callback, re-ran the effect, and
   * drained again, which called this, which set state, which rendered. It
   * issued about 120 requests to /api/state and /api/season every ten seconds
   * until the Worker started answering 503, and first paint took fifteen
   * seconds.
   */
  const refresh = useCallback(() => {
    void load()
    loadSeason()
  }, [load, loadSeason])

  /*
   * KEEPING IT TRUE. All three of these sit above the gate's early returns
   * along with everything else - React counts hooks by call order, and one
   * below a `return` is skipped on the renders that take it. That is what
   * blanked this whole app once.
   */
  const [visible, setVisible] = useState(!document.hidden)
  const [stale, setStale] = useState(false)

  /*
   * Coming back to the app is the moment somebody actually wants the truth,
   * and it costs one request. This is the fix that matters; the timer below
   * and the pull gesture are both narrower cases of it.
   */
  useEffect(() => {
    const wake = () => {
      const now = !document.hidden
      setVisible(now)
      if (!now) return
      refresh()
      void newBuildAvailable().then(setStale)
    }
    document.addEventListener('visibilitychange', wake)
    window.addEventListener('focus', wake)
    return () => {
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('focus', wake)
    }
  }, [refresh])

  // Only while somebody is watching, and faster only while something is on.
  const anyLive = !!state?.games.some((g) => g.started && g.result?.final !== true)
  useEffect(() => {
    const ms = pollMs(visible, anyLive)
    if (ms === null) return
    const id = window.setInterval(refresh, ms)
    return () => window.clearInterval(id)
  }, [refresh, visible, anyLive])

  /*
   * A pull means "give me the latest", and the latest CODE is part of that.
   * sw.js is deliberately not a cache, so a reload is the whole update - and
   * the pick queue lives in localStorage, so nothing in flight is lost by it.
   */
  const pullRefresh = useCallback(async () => {
    refresh()
    if (await newBuildAvailable()) window.location.reload()
  }, [refresh])

  /*
   * ABOVE the early returns, and it has to be.
   *
   * React counts hooks by call order, so one that sits below a `return` is
   * skipped on the renders that take it. Declared after the gate's four
   * splash returns, this produced error #310 and the app mounted nothing at
   * all - a blank white page, which is a worse failure than the request loop
   * it was written to fix.
   */

  if (gate === 'loading') return <Splash>Loading…</Splash>
  if (gate === 'join') return <JoinScreen onDone={() => { setGate('loading'); void load() }} />

  if (!state) return <Splash>{error ?? 'Loading…'}</Splash>

  if (gate === 'pick-player' || gate === 'splash') {
    return (
      <PlayerGate
        state={state}
        pending={pending}
        onChoose={(p) => { setPending(p); setGate('splash') }}
        onBack={() => { setPending(null); setGate('pick-player') }}
        onConfirmed={() => {
          /*
           * Straight to 'loading', and `pending` is NOT cleared yet.
           *
           * Clearing it first rendered PlayerGate with no pending player for
           * one frame - which is the picker list - so confirming a name
           * flashed the list of names back up before the board appeared.
           */
          setGate('loading')
          void load()
        }}
      />
    )
  }

  const me = state.players.find((p) => p.id === state.me.playerId)

  /*
   * The two badge counts. Both are about THIS player and this week: gold is
   * what you still have to do, mint is what is happening right now.
   */
  const mine = new Set(
    state.picks.filter((p) => p.playerId === state.me.playerId).map((p) => p.gameId),
  )
  const unpicked = state.games.filter((g) => !g.locked && !mine.has(g.id)).length
  const liveNow = state.games.filter((g) => g.started && !g.result?.final).length


  return (
    <div className="mx-auto flex min-h-dvh max-w-[520px] flex-col">
      {/*
       * The gesture wraps the CONTENT and stops short of the tab bar.
       *
       * It works by transforming its child, and a transformed ancestor becomes
       * the containing block for `position: fixed` - so a TabBar inside this
       * would stop being pinned to the viewport and ride up the screen on
       * every pull.
       */}
      <PullToRefresh onRefresh={pullRefresh}>
      {/* Each screen owns its own header, per the design - so the shell draws
          one only for the board. */}
      {tab === 'Board' && (
        <header className="flex items-baseline justify-between gap-3 px-[18px] pt-5 pb-3.5">
          <span className="text-[30px] font-extrabold tracking-[0.03em] uppercase">
            Pick Six
          </span>
          {/*
           * The player, and how you become somebody else.
           *
           * The design puts identity on Profile, and this is deliberately
           * quieter than it used to be - but it stays on the board, because
           * the board is the one screen where picking as the wrong kid does
           * real damage, and Dad's phone carries four people.
           */}
          <button type="button" onClick={() => setGate('pick-player')} className="text-right">
            <span className="slate-num block text-[10.5px] font-medium tracking-[0.1em] text-gold-text uppercase">
              {me?.name ?? '—'}
            </span>
            <span className="slate-num block text-[8.5px] tracking-[0.1em] text-faint uppercase">
              tap to switch
            </span>
          </button>
        </header>
      )}

      {/*
        * Board gets the strip here, directly under its header. This Week is
        * handed one as a prop so it can place it under ITS header - the strip
        * belongs below a screen's title, and every screen but this one draws
        * its own.
        *
        * Season is excluded because it IS the standings. Profile and Admin are
        * excluded because their artboards show no strip, even though the prose
        * says every screen: the drawings have been the more reliable of the
        * two sources twice already.
        */}
      {tab === 'Board' && (
        <SeasonStrip rows={season} playerId={state.me.playerId} onOpen={() => setTab('Season')} />
      )}

      {/*
        * Offered, not forced. A reload mid-pick is a worse interruption than a
        * slightly old screen, and there is no cache to fight - sw.js is not
        * one - so this only has to be taken before the next thing changes.
        */}
      {stale && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="slate-tap slate-num mx-3 mb-2.5 w-[calc(100%-24px)] border border-hairline bg-card text-[10px] font-bold tracking-[0.12em] text-live-deep uppercase"
        >
          &#9679; New version &mdash; tap to reload
        </button>
      )}

      {/*
        * The front door to notifications, above every screen.
        *
        * Same plate as the reload prompt below it and deliberately NOT mint:
        * mint is time in this palette, and "you should turn these on" is not
        * an event happening now.
        */}
      <PushBanner playerId={state.me.playerId} onOpenProfile={() => setTab('Profile')} />

      {/* Bottom padding clears the fixed tab bar, plus the home indicator. */}
      <main className="flex-1" style={{ paddingBottom: 'calc(54px + env(safe-area-inset-bottom))' }}>
        {tab === 'Board' && <Picks state={state} tz={tz} onChanged={refresh} />}
        {tab === 'This Wk' && (
          <ThisWeek
            state={state}
            tz={tz}
            strip={
              <SeasonStrip rows={season} playerId={state.me.playerId} onOpen={() => setTab('Season')} />
            }
          />
        )}
        {tab === 'Season' && <Season onViewWeek={(w) => { setWeek(w); setTab('This Wk') }} week={state.week} />}
        {tab === 'Profile' && <Profile state={state} tz={tz} onChanged={refresh} />}

        {/*
         * THE ADMIN SUB-NAV, and the reason it lives here.
         *
         * It used to be drawn inside AdminPlayers, hardcoded to show Players
         * as the active one - so the only way to reach Players was to already
         * be on Players. Nothing else ever set the view, and the screen had no
         * door at all: the first person to ask to join sat in a queue on a
         * page the commissioner could not open.
         *
         * Hidden on Add Game, which is a place you go FROM the board and back,
         * and carries its own way out.
         */}
        {tab === 'Admin' && adminView !== 'add-game' && (
          <div className="flex gap-2 px-3 pb-3">
            {(['board', 'players', 'recap'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setAdminView(t)}
                aria-current={t === adminView ? 'page' : undefined}
                className={[
                  'slate-num slate-tap relative flex flex-1 items-center justify-center rounded-[6px] text-[12px] font-bold tracking-[0.12em] uppercase',
                  t === adminView ? 'bg-ink text-ground' : 'bg-recessed text-mute',
                ].join(' ')}
              >
                {t}
                {/* The same gold count as the tab bar, repeated at the last
                    fork before the list itself. */}
                {t === 'players' && state.pendingPlayers > 0 && (
                  <span className="slate-num ml-1.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-[2px] bg-gold px-[3px] text-[9px] font-bold text-nightink">
                    {state.pendingPlayers}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {tab === 'Admin' && adminView === 'board' && (
          <AdminBoard
            tz={tz}
            onChanged={refresh}
            onAddGame={() => setAdminView('add-game')}
            nextWeekNumber={(state.weeks.at(-1)?.number ?? 0) + 1}
          />
        )}
        {tab === 'Admin' && adminView === 'recap' && <Recap state={state} tz={tz} />}
        {tab === 'Admin' && adminView === 'add-game' && (
          <AddGame tz={tz} onChanged={refresh} onBack={() => setAdminView('board')} />
        )}
        {tab === 'Admin' && adminView === 'players' && (
          <AdminPlayers
            state={state}
            onChanged={refresh}
            /* The stored role, not roster order. players[0] was Scott only
               because he was seeded first; a rename or a retirement moved the
               chip to whoever happened to be next. */
            joinCode={state.joinCode ?? undefined}
            commissionerId={state.players.find((p) => p.role === 'commissioner')?.id}
          />
        )}

        {/* The old settings screen still holds the rules and the weights, which
            the new admin screens do not cover yet. */}
        {tab === 'Admin' && adminView === 'board' && (
          <Setup state={state} onChanged={refresh} />
        )}
      </main>
      </PullToRefresh>

      <TabBar
        tab={tab}
        setTab={(t) => { setTab(t); if (t === 'Admin') setAdminView('board') }}
        isAdmin={state.me.isAdmin}
        unpicked={unpicked}
        live={liveNow}
        approvals={state.pendingPlayers}
      />
    </div>
  )
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-6 text-center text-sm text-mute">
      {children}
    </div>
  )
}

/**
 * The family code, from the path when the invite link carried one.
 *
 * `/join/1217` - what the commissioner sends. Cloudflare Pages already serves
 * the app for that path, so the link opened fine and the app simply ignored
 * it and asked for a code the person had just been given.
 *
 * Digits only, and length-checked, so a stray path segment cannot be submitted
 * as a credential and burn an attempt.
 */
function codeFromPath(): string {
  const m = /^\/join\/(\d{3,10})\/?$/.exec(window.location.pathname)
  return m ? m[1]! : ''
}

function JoinScreen({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState(codeFromPath)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /*
   * An invited person should never see a form. The code is in the link they
   * tapped, so it submits itself and they land on the picker - one tap from a
   * text message to choosing their name.
   *
   * If it fails they get the form with the code filled in, which is the honest
   * fallback: the link may be stale, and retyping it is the way back.
   */
  const [autoTried, setAutoTried] = useState(false)

  const send = async (value: string) => {
    setBusy(true)
    setErr(null)
    try {
      await api.join(value.trim())
      // Drop /join/1217 from the address bar: the code has been spent, and a
      // reload of that URL should land on the app rather than replay it.
      window.history.replaceState(null, '', '/')
      onDone()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'That did not work.')
      setBusy(false)
    }
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    void send(code)
  }

  useEffect(() => {
    const fromLink = codeFromPath()
    if (!fromLink || autoTried) return
    setAutoTried(true)
    void send(fromLink)
  }, [autoTried])

  return (
    <div className="mx-auto flex min-h-dvh max-w-[420px] flex-col justify-center px-6">
      <h1 className="text-[44px] font-bold uppercase leading-[0.95] tracking-[-0.01em]">Pick Six</h1>
      <p className="mt-3 mb-8 text-base text-mute">Ten games a week. Enter the family code.</p>
      <form onSubmit={submit}>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
          aria-label="Family code"
          className="slate-num w-full border border-hairline bg-ground px-4 py-4 text-center text-2xl tracking-[0.3em] focus:border-gold-edge focus:outline-none"
        />
        {err && <p className="mt-3 text-sm text-live">{err}</p>}
        <button
          type="submit"
          disabled={busy || code.trim().length === 0}
          className="slate-tap mt-5 w-full bg-ink px-4 py-4 text-lg font-bold uppercase tracking-[0.04em] text-card disabled:opacity-40"
        >
          {busy ? 'Checking…' : 'Go'}
        </button>
      </form>
    </div>
  )
}

function PlayerGate({
  state,
  pending,
  onChoose,
  onBack,
  onConfirmed,
}: {
  state: State
  pending: { id: string; name: string } | null
  onChoose: (p: { id: string; name: string }) => void
  onBack: () => void
  onConfirmed: () => void
}) {
  const [pin, setPin] = useState('')
  const [needsPin, setNeedsPin] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  /*
   * The fourth door, and the quietest.
   *
   * Somebody holding the family code whose name is not in the grid had no way
   * in at all - they had done everything right and were looking at a list of
   * other people. Asking is therefore an action ON the picker rather than a
   * screen of its own: the list is still the answer for almost everyone.
   */
  const [asking, setAsking] = useState(false)
  const [askName, setAskName] = useState('')
  const [askErr, setAskErr] = useState<string | null>(null)
  const [clash, setClash] = useState<string | null>(null)
  const [asked, setAsked] = useState<{ name: string; already: boolean } | null>(null)
  const [busy, setBusy] = useState(false)

  /* Who has to say yes. Stored on the roster, not guessed from its order. */
  const boss = state.players.find((p) => p.role === 'commissioner')?.name ?? 'The commissioner'

  const ask = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setAskErr(null)
    const name = askName.trim()
    try {
      const r = await api.joinRoster(name)
      /*
       * `already` means this same request is still waiting. It is not an
       * error, and telling somebody their second ask worked would leave them
       * expecting something to have changed.
       */
      setAsked({ name, already: r.already === true })
      setAsking(false)
    } catch (e2) {
      /*
       * A 409 is not a fault in this box - the name is ALREADY up there in
       * the grid - so the form gets out of the way and the server's own
       * wording goes back next to the list it is pointing at.
       */
      if (e2 instanceof ApiError && e2.status === 409) {
        setClash(e2.message)
        setAsking(false)
      } else setAskErr(e2 instanceof Error ? e2.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  if (!pending) {
    return (
      <div className="mx-auto min-h-dvh max-w-[420px] px-5 py-10">
        <h1 className="text-3xl font-bold uppercase leading-none">Who’s picking?</h1>
        <p className="mt-2 mb-6 text-sm text-mute">Tap your name.</p>
        {/* Directly above the grid, because the grid is the instruction. */}
        {clash && <p className="mb-4 text-sm text-slate">{clash}</p>}
        <ul className="grid grid-cols-2 gap-3">
          {state.players.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => { setErr(null); setPin(''); setNeedsPin(false); onChoose({ id: p.id, name: p.name }) }}
                className="slate-tap w-full border border-hairline px-3 py-5 text-lg font-bold uppercase tracking-[0.02em] active:bg-ground"
              >
                {p.name}
              </button>
            </li>
          ))}
        </ul>

        {asked ? (
          /*
           * A waiting state, and it says so. Pending is REAL - the server
           * lists active players only, so this name cannot pick and is in no
           * table until it is waved through - and dropping them into the app
           * here would be a lie the first empty board exposed anyway.
           */
          <div className="mt-6 border border-hairline bg-card p-4">
            <p className="slate-num text-[10px] tracking-[0.14em] text-mute uppercase">
              {asked.already ? 'Already asked' : 'Asked to join'}
            </p>
            <p className="mt-1.5 text-[22px] leading-none font-bold tracking-[0.02em] uppercase">
              {asked.name}
            </p>
            <p className="mt-3 text-sm text-slate">
              {boss} has to approve you first. Until then you are not on the roster and cannot
              pick — come back and tap your name once it appears above.
            </p>
          </div>
        ) : asking ? (
          <form onSubmit={ask} className="mt-6">
            <label
              htmlFor="ask-name"
              className="slate-num block text-[10px] tracking-[0.14em] text-mute uppercase"
            >
              Your name
            </label>
            <input
              id="ask-name"
              value={askName}
              onChange={(e) => setAskName(e.target.value)}
              /* The server's own limit, so the common typo never becomes a
                 round trip that comes back as a rejection. */
              maxLength={20}
              autoComplete="off"
              autoFocus
              className="slate-tap mt-2 w-full border border-hairline bg-ground px-4 text-lg focus:border-gold-edge focus:outline-none"
            />
            {askErr && <p className="mt-3 text-sm text-slate">{askErr}</p>}
            <button
              type="submit"
              disabled={busy || askName.trim().length === 0}
              className="slate-tap mt-3 w-full bg-ink px-4 py-3 text-base font-bold tracking-[0.04em] text-card uppercase disabled:opacity-40"
            >
              {busy ? 'Asking…' : 'Ask to join'}
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => { setClash(null); setAskErr(null); setAsking(true) }}
            className="slate-tap mt-6 w-full px-4 py-3 text-sm tracking-[0.06em] text-mute uppercase"
          >
            My name isn’t here
          </button>
        )}
      </div>
    )
  }

  const confirm = async () => {
    setErr(null)
    try {
      await api.select(pending.id, pin || undefined)
      onConfirmed()
    } catch (e) {
      if (e instanceof ApiError && e.message === 'needs-pin') {
        setNeedsPin(true)
        return
      }
      setErr(e instanceof Error ? e.message : 'That did not work.')
    }
  }

  /*
   * The splash. It says the name back, large, before a single pick is entered.
   * One tap, and it is the difference between "Cal's picks" and "the picks Dad
   * made while he thought he was Cal".
   */
  return (
    <div className="mx-auto flex min-h-dvh max-w-[420px] flex-col justify-center px-6 text-center">
      <p className="slate-num text-[11px] uppercase tracking-[0.16em] text-mute">Picking as</p>
      <h1 className="mt-3 text-[56px] font-bold uppercase leading-[0.9] tracking-[-0.01em] text-gold-text">
        {pending.name}
      </h1>

      {needsPin && (
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          inputMode="numeric"
          autoFocus
          aria-label="PIN"
          className="slate-num mt-8 w-full border border-hairline bg-ground px-4 py-4 text-center text-2xl tracking-[0.3em] focus:border-gold-edge focus:outline-none"
        />
      )}
      {err && <p className="mt-4 text-sm text-live">{err}</p>}

      <button
        type="button"
        onClick={confirm}
        className="slate-tap mt-8 w-full bg-ink px-4 py-4 text-xl font-bold uppercase tracking-[0.04em] text-card"
      >
        {needsPin ? 'Go' : 'It’s me — go'}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="slate-tap mt-3 w-full px-4 py-3 text-sm uppercase tracking-[0.06em] text-mute"
      >
        Not me
      </button>
    </div>
  )
}

export { Empty }
